import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tag, DollarSign, Phone, Calendar, Clock, MapPin, Truck, ExternalLink } from "lucide-react";
import { formatDate } from "../../lib/dateUtils";

const formatTime = (time) => {
  if (!time) return null;
  return `${time.slice(0, 2)}:${time.slice(2)}`;
};

export const JobDetailMeta = ({ job }) => {
  return (
    <Card className="border-slate-200 shadow-sm rounded-sm" data-testid="job-info-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-slate-800">Job Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Job Type Tags */}
        {job.job_type_tags && job.job_type_tags.length > 0 && (
          <div className="flex items-start gap-3">
            <Tag className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Job Types</p>
              <div className="flex flex-wrap gap-1">
                {job.job_type_tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-slate-50">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Billing Type */}
        <div className="flex items-start gap-3">
          <DollarSign className="w-4 h-4 text-slate-500 mt-1" />
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold">Billing Type</p>
            <Badge 
              className={`text-xs ${
                job.billing_type === "chargeable" ? "bg-emerald-100 text-emerald-800" :
                job.billing_type === "non-chargeable" ? "bg-slate-100 text-slate-800" :
                "bg-amber-100 text-amber-800"
              }`}
            >
              {job.billing_type || "Chargeable"}
            </Badge>
          </div>
        </div>

        {/* Site Contact Person */}
        {job.site_contact_person && (
          <div className="flex items-start gap-3">
            <Phone className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Site Contact</p>
              <p className="text-slate-800">{job.site_contact_person}</p>
            </div>
          </div>
        )}

        {/* Site Contact Number */}
        {job.site_contact_number && (
          <div className="flex items-start gap-3">
            <Phone className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Contact Number</p>
              <a href={`tel:${job.site_contact_number}`} className="text-blue-600 hover:underline">
                {job.site_contact_number}
              </a>
            </div>
          </div>
        )}

        {/* Scheduled Date */}
        {job.scheduled_date && (
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Scheduled Date</p>
              <p className="text-slate-800">{formatDate(job.scheduled_date)}</p>
            </div>
          </div>
        )}

        {/* Time Slot */}
        {job.scheduled_time && (
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Time Slot</p>
              <p className="text-slate-800">{formatTime(job.scheduled_time)}</p>
            </div>
          </div>
        )}

        {/* Location */}
        {job.location_address && (
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-slate-500 mt-1" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 uppercase font-semibold">Location</p>
              <div className="flex items-center gap-2">
                <p className="text-slate-800 flex-1">{job.location_address}</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                  title="Open in Google Maps"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Number */}
        {job.vehicle_number && (
          <div className="flex items-start gap-3">
            <Truck className="w-4 h-4 text-slate-500 mt-1" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Vehicle Number</p>
              <p className="text-slate-800">{job.vehicle_number}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDetailMeta;
